import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import {
  SelectFilterView,
  SelectFilterViewOptions,
} from '@/components/FilterControls/Select'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { type InfiniteSelectFilter } from '@/infra/InfiniteSelectFilter'

interface InfiniteSelectFilterViewContainerProps<
  TFilters extends Record<string, unknown>,
> {
  filter: InfiniteSelectFilter<keyof TFilters, unknown>
  testId?: string
}

/**
 * Editor for an {@link InfiniteSelectFilter}.
 */
export const InfiniteSelectFilterViewContainer = observer(
  function InfiniteSelectFilterViewContainer<
    TFilters extends Record<string, unknown>,
  >({ filter, testId }: InfiniteSelectFilterViewContainerProps<TFilters>) {
    useActivation(filter)

    return (
      <SelectFilterView
        title={filter.title}
        onClear={filter.clear}
        onSelectAll={filter.allowSelectAll ? filter.selectAll : undefined}
        onSearchChange={filter.changeSearchText}
        testId={testId && `${testId}-select-filter`}
      >
        {filter.fetch.match({
          pending: () => <ContentLoading size="compact" />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => (
            <>
              <SelectFilterViewOptions
                options={filter.items.map((o) => ({
                  value: o.value,
                  title: o.title,
                  description: o.description,
                  isSelected: filter.isSelected(o.value),
                }))}
                onOptionPress={filter.toggle}
                testId={testId && `${testId}-infinite-select-filter-option`}
              />
              <InfiniteScrollLoaderContainer
                compact
                scrollProvider={filter.scrollProvider}
              />
            </>
          ),
        })}
      </SelectFilterView>
    )
  },
)
